/**
 * Environment configuration helper
 * Works in both Vite (browser) and Node.js environments
 */

type ImportMetaLike = {
    env?: Record<string, unknown>
}

type GetEnvOptions = {
    importMeta?: ImportMetaLike | null
    processEnv?: Record<string, string | undefined> | null
}

function getSafeImportMeta(): ImportMetaLike | undefined {
    try {
        // `import.meta.env` must appear as a STATIC expression: Vite only
        // rewrites static references at build time, so reaching env through a
        // bare `import.meta` object would leave `.env` undefined in built
        // bundles — every ENV value would silently fall back to its default
        // (localhost API base, Sentry off) in stage/prod (TRA-134).
        return typeof import.meta !== 'undefined' ? { env: import.meta.env } : undefined
    } catch {
        return undefined
    }
}

export function getEnv(
    key: string,
    defaultValue: string = '',
    options: GetEnvOptions = {}
): string {
    const importMetaSource = options.importMeta ?? getSafeImportMeta()

    if (importMetaSource?.env) {
        const value = importMetaSource.env[key]
        if (typeof value === 'string') {
            return value
        }
    }

    const processEnvSource =
        options.processEnv ?? (typeof process !== 'undefined' ? process.env : undefined)

    const processValue = processEnvSource?.[key]
    if (typeof processValue === 'string') {
        return processValue
    }

    return defaultValue
}

export const ENV = {
    MODE: getEnv('MODE', 'development'),
    API_BASE_URL: getEnv('VITE_API_BASE_URL', 'http://localhost:8000'),
    AUTH_PROVIDER: getEnv('VITE_AUTH_PROVIDER', 'local'),
    SENTRY_ENABLED: getEnv('VITE_SENTRY_ENABLED', 'false'),
    SENTRY_DSN: getEnv('VITE_SENTRY_DSN', ''),
    APP_VERSION: getEnv('VITE_APP_VERSION', '0.0.0'),
    BUILD_SHA: getEnv('VITE_BUILD_SHA', ''),
    BUILD_TIME: getEnv('VITE_BUILD_TIME', ''),
    FEATURE_DEBUG: getEnv('VITE_FEATURE_DEBUG', 'false'),
    I18N_DEBUG: getEnv('VITE_I18N_DEBUG', 'false'),
    I18N_FALLBACK_LNG: getEnv('VITE_I18N_FALLBACK_LNG', ''),
} as const
